import { useState, useMemo, useCallback } from "react";
import { ArrowLeft, Search, Loader2 } from "lucide-react";
import { useBranchModal } from "@/contexts/BranchModalContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { useCart } from "@/contexts/CartContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBrand } from "@/contexts/BrandContext";
import { usePublicBranches } from "@/hooks/usePublicBranches";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import BranchCard, { type BranchInfo } from "./BranchCard";
import BusyConfirmDialog from "./BusyConfirmDialog";
import ChangeConfirmDialog from "./ChangeConfirmDialog";

export default function PickupBranchStep() {
  const { goBack, closeBranchModal, onCompleteRef } = useBranchModal();
  const { session, setBranch, setOrderType, setBranchStatus } = useSessionContext();
  const { clearCart, itemsCount } = useCart();
  const { t, language } = useLanguage();
  const { brand } = useBrand();
  const { branches, loading, error: fetchError } = usePublicBranches();

  const [search, setSearch] = useState("");
  const [busyBranch, setBusyBranch] = useState<BranchInfo | null>(null);
  const [changeBranch, setChangeBranch] = useState<BranchInfo | null>(null);

  const branchList: BranchInfo[] = useMemo(
    () =>
      branches.map((b) => ({
        id: b.id,
        name: b.name,
        name_ar: b.nameAr,
        address: b.address,
        address_ar: b.addressAr,
        phone: b.phone,
        is_open: (b as any).is_open ?? true,
        is_busy: (b as any).is_busy ?? false,
        estimated_prep_time: (b as any).estimated_prep_time ?? null,
        working_hours: b.workingHours,
        latitude: b.latitude,
        longitude: b.longitude,
        distance_km: null,
      })),
    [branches]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return branchList;
    const q = search.toLowerCase();
    return branchList.filter(
      (b) =>
        b.name.toLowerCase().includes(q) ||
        (b.name_ar && b.name_ar.includes(q)) ||
        (b.address && b.address.toLowerCase().includes(q))
    );
  }, [branchList, search]);

  const applyBranch = useCallback(
    async (branch: BranchInfo, shouldResetCart: boolean) => {
      const preserveCart = brand.productsAvailableInAllBranches && !shouldResetCart;
      const localizedName = language === "ar" && branch.name_ar ? branch.name_ar : branch.name;
      setOrderType("pickup", preserveCart);
      setBranch(
        branch.id,
        localizedName,
        1, // asap_allowed
        0, // schedule_allowed
        preserveCart
      );
      setBranchStatus(branch.is_open, branch.is_busy, null);
      if (shouldResetCart) {
        await clearCart();
      }
      onCompleteRef.current?.();
      closeBranchModal();
    },
    [
      language,
      brand.productsAvailableInAllBranches,
      setOrderType,
      setBranch,
      setBranchStatus,
      clearCart,
      closeBranchModal,
      onCompleteRef,
    ]
  );

  const handleSelect = useCallback(
    (branch: BranchInfo) => {
      const isChangingBranch = session.order.branchId && session.order.branchId !== branch.id;

      // If cart has items and changing branch and products differ by branch → confirm clear
      if (
        itemsCount > 0 &&
        isChangingBranch &&
        !brand.productsAvailableInAllBranches
      ) {
        setChangeBranch(branch);
        return;
      }
      // If branch is busy, confirm
      if (branch.is_busy) {
        setBusyBranch(branch);
        return;
      }
      applyBranch(branch, false);
    },
    [itemsCount, session.order.branchId, brand.productsAvailableInAllBranches, applyBranch]
  );

  return (
    <div className="flex flex-col max-h-[80vh]">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-border">
        <Button variant="ghost" size="icon" onClick={goBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <DialogHeader className="flex-1">
          <DialogTitle className="text-lg">{t("branch.selectBranch")}</DialogTitle>
        </DialogHeader>
      </div>

      {/* Search */}
      <div className="p-4 pb-2">
        <div className="relative">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("branch.searchBranches")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ps-10"
          />
        </div>
      </div>

      {/* Branch list */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-3">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {fetchError && (
          <div className="text-center py-12">
            <p className="text-sm text-destructive mb-3">Failed to load branches</p>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        )}

        {!loading && !fetchError && filtered.length === 0 && (
          <div className="text-center py-12">
            <span className="text-3xl mb-2 block">📍</span>
            <p className="text-sm text-muted-foreground">{t("branch.noBranches")}</p>
          </div>
        )}

        {/* Sort: open first, then busy, then closed */}
        {filtered
          .sort((a, b) => {
            if (a.is_open && !b.is_open) return -1;
            if (!a.is_open && b.is_open) return 1;
            if (a.is_busy && !b.is_busy) return 1;
            if (!a.is_busy && b.is_busy) return -1;
            return 0;
          })
          .map((branch) => (
            <BranchCard key={branch.id} branch={branch} onSelect={handleSelect} />
          ))}
      </div>

      {/* Busy confirm dialog */}
      <BusyConfirmDialog
        open={!!busyBranch}
        branchName={busyBranch?.name ?? ""}
        prepTime={busyBranch?.estimated_prep_time ?? null}
        onContinue={() => {
          if (busyBranch) applyBranch(busyBranch, false);
          setBusyBranch(null);
        }}
        onChooseAnother={() => setBusyBranch(null)}
      />

      {/* Change branch confirm */}
      <ChangeConfirmDialog
        open={!!changeBranch}
        itemsCount={itemsCount}
        branchName={session.order.branchName ?? ""}
        onKeepCurrent={() => {
          setChangeBranch(null);
          closeBranchModal();
        }}
        onSwitch={() => {
          if (changeBranch) applyBranch(changeBranch, true);
          setChangeBranch(null);
        }}
      />
    </div>
  );
}
